<template>
  <div
    v-if="!isServiceError"
    id="comments-wrapper"
    class="comments__wrapper"
  >
    <div
      id="comments"
      ref="commentsAnchorTarget"
      class="comments__anchor-target"
    />
    <div
      id="write-a-comment"
      ref="commentsInputAnchorTarget"
      class="comments__anchor-target"
    />
    <div class="comments__container">
      <div class="comments__container-inner">
        <TalkerRoot
          :id="id"
          ref="talker"
          @show-login="showLoginPopup"
          @analytics="sendAnalytics"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
  import TalkerRoot from '@devhacker/shared/plugins/talker/components/TalkerRoot.vue';
  import { useTalkerStore } from '@devhacker/shared/plugins/talker/store/talker';
  import { useAuthRedirect } from '@devhacker/shared/features/auth/utils/authRedirect';
  import { useSystemPush } from '@devhacker/shared/features/system-push/useSystemPush';
  import getSlugFromUrl from '~/utils/getSlugFromUrl';
  import { useUserStore } from '~/features/user/store';
  import { useSingleArticleStore } from '~/store/singleArticle';
  import { useServiceErrorsStore } from '~/store/errors';
  import { useAuthService } from '~/features/auth/service';
  import { scrollElementIntoViewUntilItsTopPositionIsStable } from '~/utils/scrollElementIntoViewUntilItsTopPositionIsStable';

  export default defineNuxtComponent({
    name: 'CommentsContainer',

    components: {
      TalkerRoot,
    },

    props: {
      id: {
        type: Number as PropType<number>,
        required: true,
      },
    },

    data() {
      return {
        isFirstOpenModal: true,
      };
    },

    computed: {
      article() {
        return useSingleArticleStore().article;
      },

      isServiceError() {
        return useServiceErrorsStore().commentsServiceError;
      },

      currentSlug(): string {
        return getSlugFromUrl(this.$route.fullPath);
      },

      postId(): number {
        return this.article.id;
      },

      hash(): string {
        return this.$route.hash;
      },
    },

    async beforeMount() {
      if (this.isServiceError) {
        return;
      }

      useTalkerStore().setPostId(this.postId);
    },

    async mounted() {
      if (this.isServiceError) return;

      // @ts-expect-error injected analytics helpers are not typed in options API
      this.createAnalytics();
      this.createAuthEventBus();
      this.handleHashScroll();

      await this.$nextTick();

      try {
        if (!this.postId) throw new Error('No postId provided');

        // раньше socket.io-client находился в shared и подключался там
        // но из-за проблем со сборкой, его пришлось вынести сюда

        // @ts-expect-error socket.io-client is dynamic and not typed here
        window.io = (await import('socket.io-client')).default;

        // @ts-expect-error ref typing is not inferred in options API
        this.$refs.talker.createEcho(this.postId);
      } catch (e) {
        console.error('Error while mounting comments', e);
      }
    },

    unmounted() {
      document.removeEventListener('send-analytic', this.analyticsListener);
      window.removeEventListener('success-refresh-comments', this.successRefreshListener);
      window.removeEventListener('failed-refresh-comments', this.failedRefreshListener);
    },

    methods: {
      sendAnalytics(analytics) {
        this.$sendYandexMetrika({
          level1: `${analytics.eventName}_${analytics.action}`,
        });
      },

      async handleHashScroll() {
        if (this.hash === '#write-a-comment') {
          this.scrollToInput();
        }

        if (this.hash === '#comments') {
          this.scrollToComments();
        }

        if (this.hash?.match(/comment-[0-9]*/gm)) {
          this.scrollToComment(this.hash);
        }
      },

      scrollToComments() {
        scrollElementIntoViewUntilItsTopPositionIsStable(this.$refs.commentsAnchorTarget);
      },

      scrollToComment(commentId: string) {
        const comment = document.querySelector(commentId);
        if (!comment) return;
        scrollElementIntoViewUntilItsTopPositionIsStable(comment);
      },

      // Подключение к событиям аналитики из комментов
      createAnalytics() {
        document.addEventListener('send-analytic', this.analyticsListener);
      },

      createAuthEventBus() {
        document.addEventListener('success-refresh-comments', this.successRefreshListener);
        document.addEventListener('failed-refresh-comments', this.failedRefreshListener);
      },

      analyticsListener(_event: Event) {
        // const detail = (event as CustomEvent).detail;
        // const _preparedData = this.prepareAnalyticsData(detail);
      },

      async failedRefreshListener(event: Event) {
        const authService = useAuthService();

        const detail = (event as CustomEvent).detail;
        const isBanned =
          detail?.response?.data?.data?.isBanned || detail?.request?.data?.data?.isBanned;
        if (!isBanned) {
          console.warn('refresh failed CommentsContainer', detail);
          authService.clearSessionLocally();

          // * толкер может выбросить несколько событий errorHandler и открыть модалку 2 раза
          // При ошибке refresh — redirect на auth-sso, как и раньше (пуш только для действий)
          if (this.isFirstOpenModal) {
            useAuthRedirect().login();

            this.isFirstOpenModal = false;
          }
        }
      },

      successRefreshListener() {
        console.log('refresh-success-talker-frontend');
      },

      // Преобразование данных в формат для аналитики
      // Используется формат ga4 нужно переделать на нужный формат
      prepareAnalyticModal(e: any) {
        const { eventName, action, attachments, currency, element, value, slugReferer, item } = e;

        const preparedData: any = {
          event: eventName,
          slug_location: this.currentSlug,
          slug_referer: slugReferer,
          action,
          attachments,
          currency,
          element,
          value,
          item,
        };

        return preparedData;
      },

      // Действие гостя (отправка, реакция, жалоба) — мягкий пуш вместо редиректа
      showLoginPopup(e?: any): void {
        const callback = e?.callback;
        if (callback) useUserStore().afterLoginAction = callback;

        useSystemPush().requestAuthPush();
      },

      scrollToInput() {
        scrollElementIntoViewUntilItsTopPositionIsStable(this.$refs.commentsInputAnchorTarget);
        setTimeout(() => {
          // @ts-expect-error ref typing is not inferred in options API
          this.$refs.talker.focusMainInput();
        }, 150);
      },
    },
  });
</script>

<style lang="scss" scoped>
  .comments__wrapper {
    position: relative;
  }

  .comments__anchor-target {
    display: block;
    position: absolute;
    left: 0;
    top: 0;
    width: 100%;
    height: 48px;
    transform: translateY(-100%);
    pointer-events: none;
  }

  .comment {
    background-color: green;
  }
</style>
